import { GetStaticProps } from 'next';
import Image from 'next/image';

import { api, makeDehydratedQueries } from '@endaoment-frontend/api';
import { config } from '@endaoment-frontend/config';
import { StarIcon } from '@endaoment-frontend/ui/icons';
import { Button, Pill } from '@endaoment-frontend/ui/shared';

import { FeeComparison } from '../content/advisors';
import { PortfolioAllocations } from '../content/donors';
import { DoubleFeature } from '../content/doubleFeature';
import { ImageButtonRow } from '../content/imageButtonRow';
import { MediaCarousel } from '../content/mediaCarousel';
import { MetaTags } from '../content/metaTags';
// import { NDAOTokenCallout } from '../content/ndaoTokenCallout';
import { NDAOTokenCallout } from '../content/ndaoTokenCallout';
import { SocialLinks } from '../content/socialLinks';
import { TextGridWithLogo } from '../content/textGridWithLogo';
import { TwoScreensSection } from '../content/twoScreensSection';
import bestFriendsDT from '../images/screenshots/bestFriendsDT.png';
import myFundsDT from '../images/screenshots/myFundsDT.png';
import orgCardsDT from '../images/screenshots/orgCardsDT.png';
import orgSearchDT from '../images/screenshots/orgSearchDT.png';
import reproRightsDT from '../images/screenshots/reproRightsDT.png';
import { SMStatsCard } from '../smart';
import styles from '../styles/donors.module.scss';

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      dehydratedState: await makeDehydratedQueries(
        [api.GetTotalDonations.key, api.GetTotalDonations.query],
        [api.GetTotalGrants.key, api.GetTotalGrants.query],
        [api.GetDeployedOrgs.key, api.GetDeployedOrgs.query],
      ),
    },
    // ISR every 10 minutes
    revalidate: 6000,
  };
};

const DonorsPage = () => {
  return (
    <>
      <MetaTags url="/donors" title="Endaoment | Donors" />

      <section className={styles['donor-hero']}>
        <Pill size="large" variation="blue">
          For Individual Donors
        </Pill>
        <div className={styles['donor-screenshot']}>
          <Image src={myFundsDT} priority layout="fill" alt="Product screenshot" placeholder="blur" />
        </div>
        <div className={styles['donor-cta']}>
          <h1>The first on-chain donor-advised fund. Just a transaction away. </h1>
          <Button
            as="a"
            href={`${config.clientBaseUrl}/orgs`}
            target="_blank"
            rel="noreferrer"
            className={styles['cta-button']}
            cardIcon={<StarIcon />}
          >
            <div>
              <h4>Start a Fund</h4>
              <p>Give now, get a receipt and grant over time.</p>
            </div>
          </Button>
        </div>
      </section>
      <div className={styles['stats-container']}>
        <SMStatsCard />
      </div>
      <TwoScreensSection
        title="On-Chain Donation Interface"
        image1Src={reproRightsDT}
        image2Src={orgSearchDT}
        subtext="We've built a crypto-native giving exerience from the ground up that works just like our favorite DeFi and crypto applications."
      >
        <TwoScreensSection.Button text="Launch App" href={config.clientBaseUrl} />
      </TwoScreensSection>
      <PortfolioAllocations />
      <TwoScreensSection
        title="Easy Donations to 1.5 Million Organizations"
        image1Src={orgCardsDT}
        image2Src={bestFriendsDT}
        subtext="We've paired with Candid's GuideStar nonprofit database to surface the best nonprofits in the U.S."
      >
        <TwoScreensSection.Button text="Launch App" href={config.clientBaseUrl} />
      </TwoScreensSection>
      <ImageButtonRow title="Learn More">
        <ImageButtonRow.Button
          label="Request a Demo"
          paragraph="Learn more about how the app works"
          icon={<StarIcon />}
          href="https://endaoment.typeform.com/request-demo"
        />
        <ImageButtonRow.Button
          label="Read our FAQ"
          paragraph="Find answers to your Endaoment questions"
          icon={<StarIcon />}
          href="/learn#faq-section"
        />
        <ImageButtonRow.Button
          label="Contact Us"
          paragraph="Contact an Endaoment representative"
          icon={<StarIcon />}
          href="mailto:partnerships@endaoment.org"
        />
      </ImageButtonRow>
      <DoubleFeature
        pillLeft="In-App Donations"
        pillRight="OTC Donations"
        headerLeft="Donate over 1,000+ different kinds of digital assets in-app"
        headerRight="Donate from any blockchain over-the-counter"
        extraLeft="Industry-low 1.5% fee"
        extraRight="Yes, even NFTs"
      />
      <TextGridWithLogo
        headerText="Self-directed, crypto-native, flexible giving without compromising on compliance."
        pillText="Built for Crypto Investors"
      >
        <TextGridWithLogo.Item
          header="Donor-Advised Funds"
          text="Donate any asset at any time and get a tax receipt immediately. Come back later and make grant recommendations to more than 1.5 million nonprofits whenever inspiration strikes."
          buttonText="Open a Fund"
          href={`${config.clientBaseUrl}/funds/create`}
        />
        <TextGridWithLogo.Item
          header="Direct Donations"
          text="Know where you want to give? Head straight to the profile of your favorite nonprofit and easily donate any crypto asset directly to the organization."
          buttonText="Find an Org"
          href={`${config.clientBaseUrl}/orgs`}
        />
        <TextGridWithLogo.Item
          header="Community Funds"
          text="Not sure where to give? Community Funds leverage our incredible community's knowledge base to direct donations around specific issue areas and causes."
          buttonText="Browse Community Funds"
          href={`${config.clientBaseUrl}/funds/community`}
        />
        <TextGridWithLogo.Item
          header="Endorsements"
          text="Soon you can help us curate the best and most effective nonprofits by endorsing them. Earn rewards for contributing your perspective while driving excess fee revenue to organizations you endorse."
          comingSoon
          // TODO: Figure out where this goes
          href={'#'}
        />
        <TextGridWithLogo.Item
          header="Portfolio Allocations"
          text="Don't let your philanthropic capital sit idly by while you find the perfect organization to support. Easily deploy dollars in your Endaoment Fund into DeFi yield bearing and crypto investment options."
          comingSoon
          // TODO: Figure out where this goes
          href={'#'}
        />
      </TextGridWithLogo>
      <FeeComparison />

      <NDAOTokenCallout />
      <MediaCarousel />
      <SocialLinks />
    </>
  );
};

export default DonorsPage;
